// SMS-16 Drill Planning — single source of truth
// freqMonths: how often the drill must recur (1=monthly, 2=bi-monthly, 3=quarterly, 12=annual, 0=as required)
export const DRILL_TYPES = [
  { key: 'fire',            label: 'Fire',                              freqMonths: 1,  band: 'monthly',   sms_ref: 'SMS 9.9.2',            code_ref: 'SOLAS III/19' },
  { key: 'abandon_ship',    label: 'Abandon Ship',                      freqMonths: 1,  band: 'monthly',   sms_ref: 'SMS 9.9.1',            code_ref: 'SOLAS III/19' },
  { key: 'enclosed_space',  label: 'Enclosed Space Rescue',             freqMonths: 2,  band: 'bimonthly', sms_ref: 'SMS 9.9.8',            code_ref: 'SOLAS III/19' },
  { key: 'mob',             label: 'Man Overboard / Rescue Boat Launch',freqMonths: 3,  band: 'quarterly', sms_ref: 'SMS 9.9.3',            code_ref: 'SOLAS III/19' },
  { key: 'emergency_steer', label: 'Emergency Steering',                freqMonths: 3,  band: 'quarterly', sms_ref: 'SMS 9.9.7',            code_ref: 'SOLAS V/26' },
  { key: 'collision',       label: 'Collision',                         freqMonths: 12, band: 'annual',    sms_ref: 'SMS 9.9.4',            code_ref: '', recommended: true },
  { key: 'grounding',       label: 'Grounding / Foundering',            freqMonths: 12, band: 'annual',    sms_ref: 'SMS 9.9.5',            code_ref: '', recommended: true },
  { key: 'flooding',        label: 'Flooding / Hull Failure / Excessive List', freqMonths: 12, band: 'annual', sms_ref: 'SMS 9.9.6',       code_ref: '', recommended: true },
  { key: 'emergency_tow',   label: 'Emergency Towing',                  freqMonths: 12, band: 'annual',    sms_ref: 'SMS 9.9.16',           code_ref: '', recommended: true },
  { key: 'machinery_fail',  label: 'Critical Machinery Failure or Dismasting', freqMonths: 12, band: 'annual', sms_ref: 'SMS 9.9.9 / 9.9.17', code_ref: '', recommended: true },
  { key: 'salvage',         label: 'Salvage of Own Ship',               freqMonths: 12, band: 'annual',    sms_ref: '',                     code_ref: '', recommended: true },
  { key: 'medical',         label: 'Medical Emergency',                 freqMonths: 12, band: 'annual',    sms_ref: 'SMS 9.9.10 / 9.9.11 / 9.9.12', code_ref: '', recommended: true },
  { key: 'power_fail',      label: 'Power Failure (Dead Ship)',         freqMonths: 12, band: 'annual',    sms_ref: '',                     code_ref: '', recommended: true },
  { key: 'security',        label: 'Security Drill',                    freqMonths: 12, band: 'annual',    sms_ref: '',                     code_ref: '', recommended: true },
  { key: 'pollution',       label: 'Pollution Incident',                freqMonths: 0,  band: 'asreq',     sms_ref: 'SMS 9.9.15',           code_ref: 'SOPEP / NTVRP' },
]

export const DRILL_BANDS = {
  monthly:   { label: 'Monthly',           color: '#f0a860' },
  bimonthly: { label: 'Every Other Month', color: '#f2d84e' },
  quarterly: { label: 'Quarterly',         color: '#5fd6a8' },
  annual:    { label: 'Annually',          color: '#7fb3e8' },
  asreq:     { label: 'As Required',       color: '#c8b8e8' },
}

export const drillByLabel = (label) => DRILL_TYPES.find(d => d.label === label) || null

// --- Due/overdue computation, shared by Matrix + Dashboard ---
// Returns [{ label, status:'overdue'|'due', lastDone:Date|null, monthsAgo:number|null, freqMonths }]
export function drillsNeedingAttention(records) {
  const now = new Date()
  const curMonth = now.getMonth()
  const out = []
  DRILL_TYPES.forEach(drill => {
    if (drill.freqMonths === 0) return // as-required: never flagged
    const sched = DRILL_SCHEDULE[drill.key] || []
    const mine = (records || []).filter(r => r.drill_type === drill.label && r.drill_date)
    let last = null
    mine.forEach(r => { const d = new Date(r.drill_date + 'T00:00:00'); if (!last || d > last) last = d })

    const scheduledThisMonth = sched.includes(curMonth)
    const doneThisMonth = mine.some(r => {
      const d = new Date(r.drill_date + 'T00:00:00')
      return d.getFullYear() === now.getFullYear() && d.getMonth() === curMonth
    })

    if (last) {
      const months = (now.getFullYear() - last.getFullYear()) * 12 + (now.getMonth() - last.getMonth())
      if (months > drill.freqMonths) { out.push({ label: drill.label, status: 'overdue', lastDone: last, monthsAgo: months, freqMonths: drill.freqMonths }); return }
    }
    // Due only when this month is a scheduled slot and it hasn't been done this month
    if (scheduledThisMonth && !doneThisMonth) {
      out.push({ label: drill.label, status: 'due', lastDone: last, monthsAgo: null, freqMonths: drill.freqMonths })
    }
  })
  return out
}

// Fixed calendar schedule (month indices 0=Jan … 11=Dec), mirroring the SMS-16 planning sheet.
// Quarterlies are offset from each other; annual drills are spread across the year.
export const DRILL_SCHEDULE = {
  fire:            [0,1,2,3,4,5,6,7,8,9,10,11],   // monthly
  abandon_ship:    [0,1,2,3,4,5,6,7,8,9,10,11],   // monthly
  enclosed_space:  [0,2,4,6,8,10],                // every other month
  mob:             [0,3,6,9],                     // quarterly (Jan/Apr/Jul/Oct)
  emergency_steer: [1,4,7,10],                    // quarterly, offset (Feb/May/Aug/Nov)
  collision:       [0],                           // annual — Jan
  grounding:       [1],                           // annual — Feb
  flooding:        [2],                           // annual — Mar
  emergency_tow:   [4],                           // annual — May
  machinery_fail:  [5],                           // annual — Jun
  salvage:         [6],                           // annual — Jul
  medical:         [8],                           // annual — Sep
  power_fail:      [9],                           // annual — Oct
  security:        [10],                          // annual — Nov
  pollution:       [],                            // as required — no fixed slot
}

// The day each drill type is next due (ai-next: the handover's "Drills due" and the "Run drill" watcher), from the ship's date
// todayISO. Same rules as drillsNeedingAttention, as a date: done before -> the last one + its interval in months (a date that
// has passed = overdue); never done and scheduled this month -> the last day of this month; never done, not scheduled now ->
// not listed. -> [{ key, label, due: 'YYYY-MM-DD', lastDone: 'YYYY-MM-DD' | null, overdue }], soonest first
export function drillDueDates(records, todayISO) {
  const t = String(todayISO || '').slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return []
  const [y, m] = t.split('-').map(Number)
  const plusMonths = (iso, n) => { const [a, b, c] = iso.split('-').map(Number); const last = new Date(Date.UTC(a, b - 1 + n + 1, 0)).getUTCDate(); return new Date(Date.UTC(a, b - 1 + n, Math.min(c, last))).toISOString().slice(0, 10) }
  const monthEnd = new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10)
  const out = []
  DRILL_TYPES.forEach(drill => {
    if (drill.freqMonths === 0) return
    const last = (records || []).filter(r => r.drill_type === drill.label && r.drill_date).map(r => String(r.drill_date).slice(0, 10)).filter(d => d <= t).sort().pop() || null
    let due = null
    if (last) due = plusMonths(last, drill.freqMonths)
    else if ((DRILL_SCHEDULE[drill.key] || []).includes(m - 1)) due = monthEnd
    if (due) out.push({ key: drill.key, label: drill.label, due, lastDone: last, overdue: due < t })
  })
  return out.sort((a, b) => a.due.localeCompare(b.due))
}

// a drill record is a drill that WAS held (Drill Log, ship's diary - what a surveyor reads): a day after the ship's today is a
// plan, not a record (audit-full F-2). -> null (fine) | 'invalid' | 'future'
export function drillDateProblem(dateISO, todayISO) {
  if (dateISO == null || dateISO === '') return null
  const d = String(dateISO).slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d) || isNaN(Date.parse(d + 'T00:00:00Z'))) return 'invalid'
  return /^\d{4}-\d{2}-\d{2}$/.test(String(todayISO || '').slice(0, 10)) && d > String(todayISO).slice(0, 10) ? 'future' : null
}

// planned drills (drill_due_dates, sql/audit-fixes.sql: one due date per drill type, set on the Drill Matrix or by the assistant's
// plan_drill): a plan is open until a drill of that type is recorded on or after the day it was planned
// -> [{ drill_type, due_date, notes, overdue }], soonest first
export function openPlannedDrills(planned, records, todayISO) {
  const t = String(todayISO || '').slice(0, 10)
  return (planned || []).filter(p => p && p.drill_type && p.due_date).filter(p => {
    const from = String(p.created_at || p.due_date).slice(0, 10)
    return !(records || []).some(r => r.drill_type === p.drill_type && r.drill_date && String(r.drill_date).slice(0, 10) >= from)
  }).map(p => ({ drill_type: p.drill_type, due_date: String(p.due_date).slice(0, 10), notes: p.notes || null, overdue: !!t && String(p.due_date).slice(0, 10) < t }))
    .sort((a, b) => a.due_date.localeCompare(b.due_date))
}

// the drill type from the user's words ("fire", "MOB", "enclosed space drill") -> a DRILL_TYPES entry or null
export function drillTypeFor(words) {
  const w = String(words || '').toLowerCase().replace(/\bdrills?\b/g, '').replace(/\s+/g, ' ').trim()
  if (!w) return null
  if (/\b(man overboard|mob|rescue boat)\b/.test(w)) return DRILL_TYPES.find(d => d.key === 'mob')
  return DRILL_TYPES.find(d => d.label.toLowerCase() === w || d.key === w.replace(/\s+/g, '_')) ||
    DRILL_TYPES.find(d => d.label.toLowerCase().startsWith(w)) ||
    DRILL_TYPES.find(d => w.split(/\W+/).filter(x => x.length > 2).some(x => d.label.toLowerCase().split(/\W+/).includes(x))) || null
}
